import React from 'react';
import { FiFileText, FiCheckCircle, FiClock, FiShield, FiZap, FiUsers, FiAward, FiBarChart2 } from 'react-icons/fi';

const CBSEReports = () => {
  const reportTypes = [
    {
      icon: <FiFileText className="w-6 h-6" />,
      title: 'Student Attendance Reports',
      description: 'CBSE requires detailed attendance records for all students. Schools must track daily attendance, calculate percentages, and generate reports for parents and regulatory bodies. Manual attendance tracking takes hours each week.',
      stat: 'Schools spend 5-8 hours weekly on attendance reports',
      solution: 'Truyon automates attendance tracking and generates CBSE-compliant reports instantly.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Academic Performance Reports',
      description: 'CBSE mandates comprehensive academic performance reports. Schools must track grades, calculate percentages, generate report cards, and maintain student records. Manual grade processing is time-consuming and error-prone.',
      stat: 'Schools spend 8-10 hours weekly on academic reports',
      solution: 'Truyon automates grade calculation and generates CBSE-format report cards instantly.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Registration and Enrollment',
      description: 'CBSE requires accurate student registration records, enrollment data, and student information systems. Schools must maintain up-to-date records and submit periodic reports to the board.',
      stat: 'Schools spend 5-7 hours weekly on enrollment reports',
      solution: 'Truyon manages student registration and generates enrollment reports for CBSE submission.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Examination and Assessment Reports',
      description: 'CBSE requires detailed exam records, assessment data, and result sheets. Schools must manage exam schedules, track marks, and generate results. Manual exam management is chaotic and time-consuming.',
      stat: 'Schools spend 10-15 hours monthly on exam reports',
      solution: 'Truyon automates exam management and generates CBSE-compliant result sheets.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Faculty and Staff Records',
      description: 'CBSE requires accurate records of teaching staff, qualifications, and professional development. Schools must maintain faculty records and submit periodic reports to the board.',
      stat: 'Schools spend 3-5 hours weekly on faculty reports',
      solution: 'Truyon manages faculty records and generates compliance reports automatically.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Co-Scholastic and Co-Curricular Records',
      description: 'CBSE emphasizes co-scholastic and co-curricular activities. Schools must track student participation in sports, arts, and other activities. These records are essential for holistic student development reporting.',
      stat: 'Schools spend 4-6 hours weekly on co-scholastic reports',
      solution: 'Truyon tracks co-curricular activities and generates comprehensive student profiles.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Save 40+ Hours Monthly',
      description: 'Eliminate manual report generation. Automate all CBSE compliance reporting.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Zero Errors',
      description: 'Automated reports eliminate manual calculation errors and data entry mistakes.',
      icon: <FiCheckCircle className="w-5 h-5" />
    },
    {
      title: 'Always Ready for Inspections',
      description: 'Every report is always up-to-date and accessible. Be ready for any CBSE inspection.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Standardized Format',
      description: 'All reports follow CBSE format guidelines. No formatting hassles.',
      icon: <FiFileText className="w-5 h-5" />
    },
    {
      title: 'Real-Time Data Access',
      description: 'Access any report instantly from anywhere. No waiting for manual compilation.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Reduced Staff Burnout',
      description: 'Free your staff from repetitive reporting work. Focus on teaching and improvement.',
      icon: <FiUsers className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For CBSE-affiliated schools, compliance reporting is a significant administrative burden. Attendance records, academic performance reports, enrollment data, exam results, faculty records—the list is endless. And each report must follow specific CBSE formats and guidelines.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Manual report generation is killing productivity.</strong> Staff spend hours compiling data, formatting spreadsheets, verifying calculations, and preparing reports that could be generated automatically. The cost in time, errors, and staff burnout is enormous.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how automated CBSE report generation saves schools 40+ hours a month—and why it matters.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">40+</div>
          <div className="text-sm text-slate-500">Hours saved monthly on reports</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">100%</div>
          <div className="text-sm text-slate-500">Elimination of calculation errors</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">24/7</div>
          <div className="text-sm text-slate-500">Access to any report, instantly</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">CBSE Reports That Schools Must Generate</h2>

      {/* Report Types */}
      {reportTypes.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">⏱️ {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Truyon's Solution</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Automated Report Generation Matters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Manual vs. Automated CBSE Reporting</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Manual Reporting</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Hours of manual data compilation</span></li>
              <li className="flex items-start gap-2">• <span>High risk of calculation errors</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent formatting</span></li>
              <li className="flex items-start gap-2">• <span>Report requests take days</span></li>
              <li className="flex items-start gap-2">• <span>Staff burnout and frustration</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Automated Reporting with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Reports generated in seconds</span></li>
              <li className="flex items-start gap-2">• <span>Zero calculation errors</span></li>
              <li className="flex items-start gap-2">• <span>CBSE-standardized formatting</span></li>
              <li className="flex items-start gap-2">• <span>Instant report access 24/7</span></li>
              <li className="flex items-start gap-2">• <span>Focus on teaching, not paperwork</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Time Savings Calculation */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Where the 40+ Hours Go</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
            <div className="text-sm font-semibold text-indigo-700 mb-2">Manual Process</div>
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-start gap-2">• <span>Attendance reports: 5-8 hours weekly</span></li>
              <li className="flex items-start gap-2">• <span>Academic reports: 8-10 hours weekly</span></li>
              <li className="flex items-start gap-2">• <span>Enrollment reports: 5-7 hours weekly</span></li>
              <li className="flex items-start gap-2">• <span>Faculty reports: 3-5 hours weekly</span></li>
              <li className="flex items-start gap-2">• <span>Co-scholastic reports: 4-6 hours weekly</span></li>
            </ul>
            <div className="mt-2 pt-2 border-t border-indigo-200 text-sm font-bold text-indigo-700">
              Total: 25-36 hours weekly = 100-144 hours monthly
            </div>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Automated Process with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-start gap-2">• <span>Attendance reports: 30 seconds</span></li>
              <li className="flex items-start gap-2">• <span>Academic reports: 60 seconds</span></li>
              <li className="flex items-start gap-2">• <span>Enrollment reports: 30 seconds</span></li>
              <li className="flex items-start gap-2">• <span>Faculty reports: 20 seconds</span></li>
              <li className="flex items-start gap-2">• <span>Co-scholastic reports: 30 seconds</span></li>
            </ul>
            <div className="mt-2 pt-2 border-t border-emerald-200 text-sm font-bold text-emerald-700">
              Total: Less than 3 minutes weekly = 12 minutes monthly
            </div>
          </div>
        </div>
        <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
          <p className="text-sm font-bold text-amber-700">Monthly Time Savings: 100+ hours → 12 minutes = <span className="text-amber-900">40+ hours saved!</span></p>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for CBSE Reporting</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">CBSE-Compliant Formats</h4>
            <p className="text-xs text-slate-600">All reports follow CBSE guidelines</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Instant Generation</h4>
            <p className="text-xs text-slate-600">Reports in seconds, not hours</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Zero Errors</h4>
            <p className="text-xs text-slate-600">Automated calculation and validation</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Accessible Anywhere</h4>
            <p className="text-xs text-slate-600">Reports available on any device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Comprehensive Coverage</h4>
            <p className="text-xs text-slate-600">All CBSE report types supported</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🤝</div>
            <h4 className="font-bold text-slate-900 text-sm">Staff Support</h4>
            <p className="text-xs text-slate-600">Free your staff from manual reporting</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Save 40+ Hours Monthly with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          CBSE compliance reporting doesn't have to consume your staff's time. With automated report generation, schools can save 40+ hours every month—time that can be redirected to teaching, student support, and continuous improvement.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon automates all CBSE compliance reports. Attendance, academic performance, enrollment, examinations, faculty records, and co-scholastic activities—all generated instantly in CBSE-compliant formats.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for CBSE reporting.</strong> Save time. Reduce errors. Stay compliant.
        </p>
      </div>
    </>
  );
};

export default CBSEReports;